import React, { useState, useEffect, useRef } from "react";
import firebase from "../../Firebase";
import { RecaptchaVerifier } from "firebase/auth";
import classes from "./login.module.css";
import { Constants } from "../../Constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import debounce from "lodash.debounce";
import {
  CarouselView,
  NotificationMain,
  NotificationSlider,
} from "./LoginUtils";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Urls } from "../../Urls";

const CookieBanner = ({ onAccept }) => {
  const [isVisible, setIsVisible] = useState(
    !localStorage.getItem("cookieConsent")
  );

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "true");
    setIsVisible(false);
    onAccept();
  };

  const handleReject = () => {
    localStorage.removeItem("cookieConsent");
    localStorage.clear();
    sessionStorage.clear();
    setIsVisible(false);
  };

  return (
    isVisible && (
      <div className={classes["cookie-banner"]}>
        <p>
          We use cookies to enhance your experience. By continuing to browse,
          you agree to our use of cookies.{" "}
          <a
            href="https://lntermsandcondition.blob.core.windows.net/tnc/privacypolicy.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
          {/*  */}
        </p>
        <button onClick={handleAccept} className={classes["accept-btn"]}>
          Accept Cookies
        </button>
        <button onClick={handleReject} className={classes["reject-btn"]}>
          Reject
        </button>
      </div>
    )
  );
};

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState();
  const [code, setCode] = useState();
  const [captchaVerifier, setCaptchaVerifier] = useState();
  const [acceptedTermsAndCondition, setAcceptedTermsAndCondition] =
    useState(true);
  const [showTermsConditionsErrorMessage, setShowTermsConditionsErrorMessage] =
    useState("hide");
  const [showInvalidPhoneNumberMessage, setShowInvalidPhoneNumberMessage] =
    useState("hide");
  const [showInvalidVerificationCode, setShowInvalidVerificationCode] =
    useState("hide");
  const [verificationErrorMessage, setVerificationErrorMessage] = useState("");
  const [showVerifyingDialog, setShowVerifyingDialog] = useState(false);
  const [countryCode, setCountryCode] = useState("IN");
  const [isDisabled, setIsDisabled] = useState(false);
  const [cookiesAccepted, setCookiesAccepted] = useState(
    !!localStorage.getItem("cookieConsent")
  );
  const inputRef = useRef(null);

  const FirebaseErrors = [
    {
      code: "invalid-verification-code",
      message: "Please enter valid verification code",
    },
    {
      code: "code-expired",
      message: "Verification code is expired",
    },
    {
      code: "too-many-requests",
      message:
        "We have blocked all requests from this device due to too many requests. Try again later.",
    },
  ];

  useEffect(() => {
    const storedCountryCode = localStorage.getItem("countryCode");
    if (storedCountryCode) {
      setCountryCode(storedCountryCode);
    }

    window.recaptchaVerifier = new RecaptchaVerifier(
      "recaptcha-container",
      {
        size: "invisible",
      },
      firebase.getAuth
    );
  }, []);

  const phoneNumberValidation = () => {
    if (!phoneNumber || !phoneNumber.trim()) return false;

    const _isValid = isValidPhoneNumber(phoneNumber.trim());

    if (!_isValid) return false;

    return true;
  };

  const isValidVerificationCode = () => {
    if (!code || !code.trim()) return false;
    if (code.length !== 6) return false;
    if (!new RegExp(Constants.OnlyNumberRegEx).test(code)) return false;
    return true;
  };

  const getFirebaseErrorMessage = (error) => {
    const firebaseErrors = FirebaseErrors.filter((e) =>
      error.code.includes(e.code)
    );
    return firebaseErrors && firebaseErrors.length
      ? firebaseErrors[0].message
      : "Invalid request";
  };

  const login = async () => {
    if (!phoneNumberValidation()) {
      setShowInvalidPhoneNumberMessage("show");
      return;
    }

    if (!acceptedTermsAndCondition) {
      setShowTermsConditionsErrorMessage("show");
      return;
    }

    localStorage.setItem("countryCode", countryCode);
    setShowVerifyingDialog(true);
    setIsDisabled(true);

    try {
      const confirmationResult = await firebase.login(phoneNumber);

      setCaptchaVerifier(confirmationResult);
      setShowVerifyingDialog(false);
      setIsDisabled(false);
    } catch (error) {
      console.error("Firebase Login Error:", error);

      setVerificationErrorMessage(getFirebaseErrorMessage(error));
      setShowVerifyingDialog(false);
      setIsDisabled(false);
    } finally {
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    }
  };

  const verifyCode = async () => {
    if (!isValidVerificationCode()) {
      setShowInvalidVerificationCode("show");
      return;
    }

    setIsDisabled(true);

    await captchaVerifier
      .confirm(code)
      .then(function () {
        localStorage.setItem("loggedin", "yes");
      })
      .catch(function (error) {
        console.log(error);
        setVerificationErrorMessage(getFirebaseErrorMessage(error));
      })
      .finally(() => {
        setIsDisabled(false);
      });
  };

  const NotificationDialog = ({ children }) => {
    return <div className={classes["notification-dialog"]}>{children}</div>;
  };

  const VerificationDialog = () => {
    return (
      <NotificationDialog>
        <FontAwesomeIcon icon={["fas", "spinner"]} size="1x" spin />
        <span className={classes["dialog-message"]}>Verifying...</span>
      </NotificationDialog>
    );
  };

  return (
    <section
      className={`${classes["Login_Home_Page"]} row`}
      id="AuthenticationDiv"
      style={{ backgroundColor: "white" }}
    >
      <CookieBanner onAccept={() => setCookiesAccepted(true)} />
      <CarouselView />
      <NotificationSlider />
      <NotificationMain />

      <article
        className="col-md-5"
        style={{ backgroundColor: "white !important" }}
      >
        <div className={classes["login-container"]}>
          {captchaVerifier ? (
            <>
              <h6>Enter verification code sent to</h6>
              <h6>{phoneNumber}</h6>
              <input
                className={classes["input-field-verify"]}
                ref={inputRef}
                autoFocus={true}
                placeholder="Verification code"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    verifyCode();
                  }
                }}
                onChange={(e) => {
                  setCode(e.target.value);
                  setShowInvalidVerificationCode("hide");
                }}
                disabled={isDisabled}
              />
              {showInvalidVerificationCode === "show" ? (
                <span className={classes["terms-conditions-error-message"]}>
                  Please enter 6 digit code
                </span>
              ) : null}
              {verificationErrorMessage ? (
                <span className={classes["terms-conditions-error-message"]}>
                  {verificationErrorMessage}
                </span>
              ) : null}
              <br />
              <button
                className={classes["button-verify"] + " btn"}
                onClick={debounce(verifyCode, 500)}
                disabled={isDisabled}
              >
                LOGIN
              </button>
            </>
          ) : (
            <>
              <div className={`pb-5 ${classes["Login-Logo-Container"]}`}>
                <img
                  alt="logo"
                  height={"186px"}
                  className={classes["logo"]}
                  src={Urls.LorriLogoLogin}
                />
              </div>
              <div className={classes["login-form"]}>
                <h6 className="mb-2">Enter your phone number</h6>
                <div className={classes["country-flag"]}>
                  <PhoneInput
                    disabled={isDisabled}
                    international
                    defaultCountry={countryCode}
                    numberInputProps={{
                      onKeyDown: (e) => {
                        if (e.key === "Enter") {
                          login();
                        }
                      },
                    }}
                    value={phoneNumber}
                    onCountryChange={(country) => {
                      setCountryCode(country);
                    }}
                    onChange={(value) => {
                      setPhoneNumber(value);
                    }}
                    className={classes["input-field"]}
                    // maxLength={15}
                    style={{ width: "100%" }}
                  />
                </div>
                {showInvalidPhoneNumberMessage === "show" ? (
                  <span className={classes["terms-conditions-error-message"]}>
                    Enter a valid phone number
                  </span>
                ) : null}
                {verificationErrorMessage ? (
                  <span className={classes["terms-conditions-error-message"]}>
                    {verificationErrorMessage}
                  </span>
                ) : null}
                <br />
                <button
                  className={classes["button-verify"] + " btn"}
                  id="recaptcha-container"
                  type="button"
                  disabled={isDisabled}
                  onClick={login}
                  style={{ width: "100%" }}
                  // onKeyDown={(e) => {
                  //   if (e.key === "Enter") {
                  //     login();
                  //   }
                  // }}
                >
                  VERIFY
                </button>
                <br />
                <label className={classes["verify-message"]}>
                  By tapping Verify, an SMS may be sent. Message & data rates
                  may apply.
                </label>
                {showVerifyingDialog ? <VerificationDialog /> : null}
              </div>
              <div className={classes["terms-conditions-container"]}>
                <label>
                  <input
                    style={{ width: "auto" }}
                    className={classes["terms-conditions-checkbox"]}
                    type="checkbox"
                    defaultChecked={true}
                    disabled={isDisabled}
                    onChange={(event) => {
                      setAcceptedTermsAndCondition(event.target.checked);
                      setShowTermsConditionsErrorMessage(
                        event.target.checked ? "hide" : "show"
                      );
                    }}
                  />
                  <span className={classes["terms-conditions-message"]}>
                    By Signing in, you are accepting LogisticsNow{" "}
                    <a
                      className={classes["terms-conditions-anchor"]}
                      href="https://lntermsandconditions.blob.core.windows.net/tnc/tnc.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Use
                    </a>{" "}
                    and{" "}
                    <a
                      className={classes["terms-conditions-anchor"]}
                      href="https://lntermsandcondition.blob.core.windows.net/tnc/privacypolicy.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>{" "}
                  </span>
                </label>
              </div>
              {showTermsConditionsErrorMessage === "show" ? (
                <span className={classes["terms-conditions-error-message"]}>
                  Please accept the terms and conditions
                </span>
              ) : null}
            </>
          )}
        </div>
      </article>
    </section>
  );
};

export default Login;
